import { useEffect, useRef } from "react";
import popupStyles from "../planChangeImpactConfirmation/planChangeImpactConfirmation.module.css";
import { IPopupWithoutProps } from "../../../types/types";
import venchyImg from "../../../images/questionnaire_doctor.png";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { SeatSubscriptionDto } from "../../../services/api/featureCreditPackagesApi";
import { useGetResourceCapsImpactQuery } from "../../../services/api/resourceCaps.api";
import { renderResourceImpactLine } from "../resourceCapImpactLine";
import { cancelPromiseDate } from "../../../utils/seatSubscriptions";
import { formatDateInIsrael } from "../../../utils/utils";

interface CancelSeatPackConfirmationProps extends IPopupWithoutProps {
  row: SeatSubscriptionDto;
  isCancelling: boolean;
  onConfirm: () => void;
}

function CancelSeatPackConfirmation({
  onClose,
  onConfirm,
  row,
  isCancelling,
}: CancelSeatPackConfirmationProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);

  // 404s `unknown_subscription` only for an id that isn't the caller's own live pack, which can't
  // happen here (this dialog only opens from a row already on screen) — same as any other failure,
  // `data` just stays undefined and the dialog falls back to showing no impact, never an error box.
  const { data: impact } = useGetResourceCapsImpactQuery({ endingSeatSubscriptionId: row.id });

  const therapistsImpact = impact && renderResourceImpactLine({
    testId: "seat-pack-cancel-impact-therapists",
    resource: impact.therapists,
    summaryKey: "SEAT_PACK_CANCEL_IMPACT_THERAPISTS",
  });
  const patientsImpact = impact && renderResourceImpactLine({
    testId: "seat-pack-cancel-impact-patients",
    resource: impact.patients,
    summaryKey: "SEAT_PACK_CANCEL_IMPACT_PATIENTS",
  });
  const hasCapImpact = !!(therapistsImpact || patientsImpact);

  // Land on "Keep pack": the safe half of the choice, and the same thing Esc resolves to via
  // ModalOverlay → Modal's onClose.
  useEffect(() => {
    keepButtonRef.current?.focus();
  }, []);

  // The shared Modal has no focus trap, so keep Tab inside this dialog rather than letting it walk
  // out into the account screen behind it. Scoped here on purpose — fixing Modal globally would
  // touch every modal in the app.
  const trapTab = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;

    const buttons = Array.from(
      dialogRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? []
    );
    if (buttons.length === 0) return;

    const edge = event.shiftKey ? buttons[0] : buttons[buttons.length - 1];
    if (document.activeElement === edge) {
      event.preventDefault();
      (event.shiftKey ? buttons[buttons.length - 1] : buttons[0]).focus();
    }
  };

  // "at least <date>" is an exact lower bound, not vagueness: a concurrent renewal between the GET
  // that filled this dialog and the Cancel POST can only push the period forward, and the one case
  // that ends sooner (no paid time left) is routed to the imminent copy by cancelPromiseDate. So the
  // banner naming a later EffectiveAt afterwards reads as a clarification, not a contradiction.
  const promisedDate = cancelPromiseDate(row);
  const message = promisedDate
    ? t("SEAT_PACK_CANCEL_CONFIRM_TEXT", {
        name: row.name,
        count: row.seats,
        date: formatDateInIsrael(promisedDate),
      })
    : t("SEAT_PACK_CANCEL_IMMINENT_CONFIRM", { name: row.name, count: row.seats });

  return (
    <article
      className={popupStyles.popup}
      data-testid="cancel-seat-pack-popup"
      ref={dialogRef}
      onKeyDown={trapTab}
    >
      <img className={popupStyles.popup__img} src={venchyImg} alt={t("VENCHI")} />
      <h2 className={popupStyles.popup__title}>
        {t("SEAT_PACK_CANCEL_CONFIRM_TITLE", { name: row.name })}
      </h2>
      <p className={popupStyles.popup__text}>{message}</p>

      {hasCapImpact && (
        <>
          {therapistsImpact}
          {patientsImpact}
          <p className={popupStyles.popup__warningDetail} role="alert">
            {t("SEAT_PACK_CANCEL_IMPACT_DEACTIVATION_NOTE")}
          </p>
        </>
      )}

      <div className={popupStyles.popup__buttons}>
        {/* Never a button labelled just "Cancel": in a dialog about cancelling a subscription it is
            genuinely ambiguous about which thing it cancels. */}
        <button
          className={`button ${popupStyles.popup__confirmBtn} hover`}
          data-testid="confirm-cancel-seat-pack-button"
          onClick={onConfirm}
          disabled={isCancelling}
        >
          {isCancelling ? t("LOADING") : t("SEAT_PACK_CONFIRM_CANCEL")}
        </button>
        <button
          className={`button ${popupStyles.popup__cancelBtn} hover`}
          data-testid="keep-seat-pack-button"
          ref={keepButtonRef}
          onClick={onClose}
        >
          {t("SEAT_PACK_KEEP")}
        </button>
      </div>
    </article>
  );
}

export default CancelSeatPackConfirmation;
